import type { Category } from './schemas.js';

const STAMP = '2024-01-01T00:00:00.000Z';

/**
 * The categories every install starts with. They can be renamed, archived or
 * added to; `transfers` is the only one that is excluded from spending, which
 * is what keeps a top-up out of the monthly total.
 */
export const DEFAULT_CATEGORIES: Category[] = [
  { id: 'food-and-drinks', name: 'Food and Drinks', tone: 'amber', icon: '🍜' },
  { id: 'groceries', name: 'Groceries', tone: 'lime', icon: '🛒' },
  { id: 'transport', name: 'Transport', tone: 'blue', icon: '🚗' },
  { id: 'bills', name: 'Bills', tone: 'slate', icon: '🧾' },
  { id: 'shopping', name: 'Shopping', tone: 'rose', icon: '🛍️' },
  { id: 'entertainment', name: 'Entertainment', tone: 'violet', icon: '🎬' },
  { id: 'health', name: 'Health', tone: 'green', icon: '💊' },
  { id: 'education', name: 'Education', tone: 'cyan', icon: '📚' },
  { id: 'travel', name: 'Travel', tone: 'teal', icon: '✈️' },
  { id: 'subscriptions', name: 'Subscriptions', tone: 'orange', icon: '🔁' },
  { id: 'transfers', name: 'Transfers', tone: 'slate', icon: '↔️' },
  { id: 'other', name: 'Other', tone: 'slate', icon: '•' },
].map((category, index) => ({
  ...category,
  isBuiltIn: true,
  excludeFromSpending: category.id === 'transfers',
  archived: false,
  sortOrder: index,
  updatedAt: STAMP,
})) as Category[];

export const DEFAULT_CATEGORY_ID = 'other';
export const TRANSFER_CATEGORY_ID = 'transfers';

export function findCategory(
  categories: readonly Category[],
  id: string | undefined,
): Category | undefined {
  if (!id) return undefined;
  return categories.find((category) => category.id === id);
}

export function categoryName(categories: readonly Category[], id: string): string {
  return findCategory(categories, id)?.name ?? 'Uncategorised';
}

/** Turn a typed category name into a stable id. */
export function slugifyCategoryName(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return slug || 'category';
}

/** Make a slug unique against the ids already in use. */
export function uniqueCategoryId(name: string, existing: readonly string[]): string {
  const base = slugifyCategoryName(name);
  if (!existing.includes(base)) return base;
  for (let suffix = 2; suffix < 1000; suffix += 1) {
    const candidate = `${base}-${suffix}`;
    if (!existing.includes(candidate)) return candidate;
  }
  throw new Error('Could not allocate a unique category id');
}
